import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { StewieLogo } from '../components/StewieLogo';
import {
  ArrowLeft, RefreshCw, Calendar, ChevronDown,
  AlertTriangle, Shield, Users, MapPin, TrendingUp,
  FileText, Eye,
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_COLORS = {
  discipline: '#ef4444', overtime: '#f59e0b', safety: '#f97316',
  pay: '#10b981', seniority: '#6366f1', 'job assignment': '#8b5cf6',
  harassment: '#ec4899', 'contract violation': '#3b82f6',
  'harassment / treatment': '#ec4899', general: '#64748b', unknown: '#475569',
};

const STRENGTH_COLORS = { Strong: '#10b981', Medium: '#f59e0b', Weak: '#ef4444', Unknown: '#64748b' };

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
}

export default function GrievanceCaseIntelligencePage({ user }) {
  const navigate = useNavigate();
  const [days, setDays] = useState(180);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [supervisors, setSupervisors] = useState([]);
  const [patterns, setPatterns] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [recent, setRecent] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const [ovRes, supRes, patRes, tlRes, recRes] = await Promise.all([
        axios.get(`${API}/api/grievance/analytics/overview?days=${days}`, { headers }),
        axios.get(`${API}/api/grievance/analytics/supervisors?days=${days}`, { headers }),
        axios.get(`${API}/api/grievance/analytics/patterns?days=${days}`, { headers }),
        axios.get(`${API}/api/grievance/analytics/timeline?days=${days}`, { headers }),
        axios.get(`${API}/api/grievance/analytics/recent?limit=20`, { headers }),
      ]);
      setOverview(ovRes.data);
      setSupervisors(supRes.data.supervisors || []);
      setPatterns(patRes.data);
      setTimeline(tlRes.data.timeline || []);
      setRecent(recRes.data.cases || []);
    } catch (err) {
      console.error('Failed to fetch case intelligence:', err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const pieData = (overview?.byType || []).map(t => ({
    name: t.type, value: t.count,
  }));

  const flaggedSups = supervisors.filter(s => s.flagged);
  const criticalAlerts = (patterns?.alerts || []).filter(a => a.severity === 'critical');
  const warningAlerts = (patterns?.alerts || []).filter(a => a.severity === 'warning');

  return (
    <div className="ci-dash" data-testid="case-intelligence-dashboard">
      <style>{ciStyles}</style>

      {/* Header */}
      <header className="ci-header">
        <div className="ci-header-left">
          <button className="ci-back" onClick={() => navigate('/')} data-testid="ci-back-btn">
            <ArrowLeft size={18} /> Back
          </button>
          <StewieLogo size="sm" />
          <div>
            <h1 className="ci-title">Case Intelligence</h1>
            <p className="ci-subtitle">Grievance patterns, supervisor hotspots & trend analysis</p>
          </div>
        </div>
        <div className="ci-header-right">
          <div className="ci-range-select">
            <Calendar size={14} />
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="ci-days-select">
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 6 months</option>
              <option value={365}>Last year</option>
            </select>
            <ChevronDown size={14} />
          </div>
          <button className="ci-refresh" onClick={fetchData} disabled={loading} data-testid="ci-refresh-btn">
            <RefreshCw size={16} className={loading ? 'ci-spin' : ''} /> Refresh
          </button>
        </div>
      </header>

      {loading && !overview ? (
        <div className="ci-loading" data-testid="ci-loading">
          <RefreshCw size={32} className="ci-spin" />
          <p>Loading case intelligence...</p>
        </div>
      ) : (
        <>
          {/* Stat Cards */}
          <div className="ci-stats-grid" data-testid="ci-stat-cards">
            <StatCard icon={FileText} label="Total Grievances" value={overview?.totalGrievances || 0} color="#3b82f6" />
            <StatCard icon={Shield} label="Grievance Types" value={(overview?.byType || []).length} color="#10b981" />
            <StatCard icon={AlertTriangle} label="Flagged Supervisors" value={overview?.flaggedSupervisors || 0} sub={`${PATTERN_THRESHOLD}+ cases`} color="#ef4444" />
            <StatCard icon={TrendingUp} label="Avg Missing Items" value={overview?.avgMissingItems || 0} color="#f59e0b" />
          </div>

          {/* Alert Banner */}
          {criticalAlerts.length > 0 && (
            <div className="ci-alert-banner ci-alert-critical" data-testid="ci-critical-alerts">
              <AlertTriangle size={18} />
              <div>
                <strong>{criticalAlerts.length} Critical Pattern{criticalAlerts.length > 1 ? 's' : ''} Detected</strong>
                <span>{criticalAlerts.map(a => a.message).join(' | ')}</span>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="ci-tabs" data-testid="ci-tabs">
            {['overview', 'supervisors', 'patterns', 'cases'].map(tab => (
              <button
                key={tab}
                className={`ci-tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
                data-testid={`ci-tab-${tab}`}
              >
                {tab === 'overview' && 'Overview'}
                {tab === 'supervisors' && `Supervisors${flaggedSups.length ? ` (${flaggedSups.length})` : ''}`}
                {tab === 'patterns' && 'Patterns & Alerts'}
                {tab === 'cases' && 'Recent Cases'}
              </button>
            ))}
          </div>

          {/* TAB: Overview */}
          {activeTab === 'overview' && (
            <div className="ci-grid-2" data-testid="ci-overview-tab">
              {/* Type Distribution */}
              <div className="ci-card">
                <h3 className="ci-card-title">Grievance Type Distribution</h3>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, value }) => `${name} (${value})`}>
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={TYPE_COLORS[entry.name] || '#64748b'} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <div className="ci-empty">No data available</div>}
              </div>

              {/* Evidence Strength */}
              <div className="ci-card">
                <h3 className="ci-card-title">Evidence Strength</h3>
                <div className="ci-strength-grid">
                  {Object.entries(overview?.evidenceStrength || {}).map(([k, v]) => (
                    <div key={k} className="ci-strength-item">
                      <div className="ci-strength-dot" style={{ background: STRENGTH_COLORS[k] || '#64748b' }} />
                      <span className="ci-strength-label">{k}</span>
                      <span className="ci-strength-value">{v}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 20 }}>
                  <h4 className="ci-card-subtitle">Generation Mode</h4>
                  <div className="ci-strength-grid">
                    {Object.entries(overview?.generationMode || {}).map(([k, v]) => (
                      <div key={k} className="ci-strength-item">
                        <div className="ci-strength-dot" style={{ background: k === 'ai_enhanced' ? '#8b5cf6' : '#3b82f6' }} />
                        <span className="ci-strength-label">{k === 'ai_enhanced' ? 'AI Enhanced' : 'Deterministic'}</span>
                        <span className="ci-strength-value">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="ci-card ci-card-wide">
                <h3 className="ci-card-title">Grievance Volume Over Time</h3>
                {timeline.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={timeline}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="week" tick={{ fill: '#64748b', fontSize: 11 }} />
                      <YAxis tick={{ fill: '#64748b', fontSize: 11 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                      <Line type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4, fill: '#3b82f6' }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <div className="ci-empty">No timeline data</div>}
              </div>

              {/* Location & Department */}
              <div className="ci-card">
                <h3 className="ci-card-title">By Location</h3>
                {(patterns?.locationBreakdown || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={patterns.locationBreakdown} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis type="number" tick={{ fill: '#64748b', fontSize: 11 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="location" tick={{ fill: '#94a3b8', fontSize: 11 }} width={140} />
                      <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                      <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="ci-empty">No location data</div>}
              </div>

              <div className="ci-card">
                <h3 className="ci-card-title">By Department</h3>
                {(patterns?.departmentBreakdown || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={patterns.departmentBreakdown} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis type="number" tick={{ fill: '#64748b', fontSize: 11 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="department" tick={{ fill: '#94a3b8', fontSize: 11 }} width={140} />
                      <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                      <Bar dataKey="count" fill="#10b981" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="ci-empty">No department data</div>}
              </div>
            </div>
          )}

          {/* TAB: Supervisors */}
          {activeTab === 'supervisors' && (
            <div data-testid="ci-supervisors-tab">
              <div className="ci-card">
                <h3 className="ci-card-title">Supervisor Hotspot Analysis</h3>
                <p className="ci-card-desc">Supervisors with {PATTERN_THRESHOLD}+ grievances are flagged for review</p>
                <div className="ci-table-wrap">
                  <table className="ci-table">
                    <thead>
                      <tr>
                        <th>Supervisor</th>
                        <th>Cases</th>
                        <th>Status</th>
                        <th>Top Type</th>
                        <th>Departments</th>
                        <th>Last Grievance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supervisors.map((s, i) => {
                        const topType = s.typeBreakdown ? Object.entries(s.typeBreakdown).sort((a, b) => b[1] - a[1])[0] : null;
                        return (
                          <tr key={i} data-testid={`ci-supervisor-row-${i}`}>
                            <td style={{ fontWeight: 600, color: '#f8fafc' }}>{s.supervisor}</td>
                            <td>
                              <span className="ci-count-badge" style={{ background: s.flagged ? '#ef444420' : '#3b82f620', color: s.flagged ? '#fca5a5' : '#93c5fd' }}>
                                {s.count}
                              </span>
                            </td>
                            <td>
                              {s.flagged ? (
                                <span className="ci-flag-badge ci-flag-danger" data-testid={`ci-flagged-${i}`}>
                                  <AlertTriangle size={12} /> Flagged
                                </span>
                              ) : (
                                <span className="ci-flag-badge ci-flag-ok">Normal</span>
                              )}
                            </td>
                            <td>
                              {topType && (
                                <span className="ci-type-badge" style={{ background: `${TYPE_COLORS[topType[0]] || '#64748b'}20`, color: TYPE_COLORS[topType[0]] || '#94a3b8' }}>
                                  {topType[0]} ({topType[1]})
                                </span>
                              )}
                            </td>
                            <td style={{ color: '#94a3b8', fontSize: 12 }}>{(s.departments || []).join(', ')}</td>
                            <td className="ci-time">{fmtDate(s.latestGrievance)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB: Patterns & Alerts */}
          {activeTab === 'patterns' && (
            <div data-testid="ci-patterns-tab">
              <div className="ci-card">
                <h3 className="ci-card-title">Active Alerts</h3>
                {(patterns?.alerts || []).length > 0 ? (
                  <div className="ci-alerts-list">
                    {(patterns.alerts || []).map((a, i) => (
                      <div key={i} className={`ci-alert-item ci-alert-${a.severity}`} data-testid={`ci-alert-${i}`}>
                        <div className="ci-alert-icon">
                          {a.severity === 'critical' ? <AlertTriangle size={16} /> : <Eye size={16} />}
                        </div>
                        <div className="ci-alert-content">
                          <span className="ci-alert-type-badge">{a.type.replace('_', ' ')}</span>
                          <span className="ci-alert-msg">{a.message}</span>
                        </div>
                        <span className={`ci-severity-badge ci-sev-${a.severity}`}>{a.severity}</span>
                      </div>
                    ))}
                  </div>
                ) : <div className="ci-empty">No active alerts</div>}
              </div>
            </div>
          )}

          {/* TAB: Recent Cases */}
          {activeTab === 'cases' && (
            <div data-testid="ci-cases-tab">
              <div className="ci-card">
                <h3 className="ci-card-title">Recent Grievance Cases</h3>
                <div className="ci-table-wrap">
                  <table className="ci-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Member</th>
                        <th>Supervisor</th>
                        <th>Location</th>
                        <th>Evidence</th>
                        <th>Summary</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.map((c, i) => (
                        <tr key={i} data-testid={`ci-case-row-${i}`}>
                          <td className="ci-time">{fmtDate(c.createdAt)}</td>
                          <td>
                            <span className="ci-type-badge" style={{ background: `${TYPE_COLORS[c.grievanceType] || '#64748b'}20`, color: TYPE_COLORS[c.grievanceType] || '#94a3b8' }}>
                              {c.grievanceType || 'unknown'}
                            </span>
                          </td>
                          <td style={{ color: '#e2e8f0' }}>{c.member || '—'}</td>
                          <td style={{ color: '#e2e8f0' }}>{c.supervisor || '—'}</td>
                          <td style={{ color: '#94a3b8', fontSize: 12 }}>{c.location || '—'}</td>
                          <td>
                            <span className="ci-strength-pill" style={{ background: `${STRENGTH_COLORS[c.evidenceStrength] || '#64748b'}20`, color: STRENGTH_COLORS[c.evidenceStrength] || '#94a3b8' }}>
                              {c.evidenceStrength || '—'}
                            </span>
                          </td>
                          <td style={{ color: '#94a3b8', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.summary || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const PATTERN_THRESHOLD = 3;

function StatCard({ icon: Icon, label, value, sub, color = '#3b82f6' }) {
  return (
    <div className="ci-stat-card" data-testid={`ci-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="ci-stat-icon" style={{ backgroundColor: `${color}18`, color }}>
        <Icon size={22} />
      </div>
      <div className="ci-stat-info">
        <span className="ci-stat-value">{value}</span>
        <span className="ci-stat-label">{label}</span>
        {sub && <span className="ci-stat-sub">{sub}</span>}
      </div>
    </div>
  );
}

const ciStyles = `
  .ci-dash {
    min-height: 100vh;
    background: #0b1120;
    color: #e2e8f0;
    padding: 24px 32px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  .ci-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 24px; flex-wrap: wrap; gap: 16px;
  }
  .ci-header-left { display: flex; align-items: center; gap: 12px; }
  .ci-header-right { display: flex; align-items: center; gap: 12px; }
  .ci-back {
    display: flex; align-items: center; gap: 6px;
    background: transparent; color: #94a3b8; border: 1px solid #334155;
    padding: 8px 14px; border-radius: 8px; cursor: pointer; font-size: 13px;
    transition: all 0.15s;
  }
  .ci-back:hover { color: #e2e8f0; border-color: #475569; }
  .ci-title { font-size: 22px; font-weight: 700; color: #f8fafc; margin: 0; }
  .ci-subtitle { font-size: 13px; color: #64748b; margin: 2px 0 0; }
  .ci-range-select {
    display: flex; align-items: center; gap: 6px;
    background: #111827; border: 1px solid #1e293b; border-radius: 8px;
    padding: 6px 12px; color: #94a3b8; font-size: 13px;
  }
  .ci-range-select select {
    background: transparent; color: #e2e8f0; border: none; font-size: 13px;
    cursor: pointer; outline: none; -webkit-appearance: none;
  }
  .ci-range-select select option { background: #1e293b; }
  .ci-refresh {
    display: flex; align-items: center; gap: 6px;
    background: #3b82f6; color: white; border: none; padding: 8px 16px;
    border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500;
  }
  .ci-refresh:hover { background: #2563eb; }
  .ci-refresh:disabled { opacity: 0.6; }

  .ci-loading {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    min-height: 300px; color: #64748b; gap: 12px;
  }
  .ci-spin { animation: ci-spin-anim 1s linear infinite; }
  @keyframes ci-spin-anim { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

  /* Stat Cards */
  .ci-stats-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 14px; margin-bottom: 20px;
  }
  .ci-stat-card {
    background: #111827; border: 1px solid #1e293b; border-radius: 12px;
    padding: 18px; display: flex; align-items: center; gap: 14px;
    transition: border-color 0.2s;
  }
  .ci-stat-card:hover { border-color: #334155; }
  .ci-stat-icon {
    width: 42px; height: 42px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .ci-stat-info { display: flex; flex-direction: column; }
  .ci-stat-value { font-size: 22px; font-weight: 700; color: #f8fafc; }
  .ci-stat-label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
  .ci-stat-sub { font-size: 10px; color: #475569; }

  /* Alert Banner */
  .ci-alert-banner {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 14px 18px; border-radius: 10px; margin-bottom: 20px;
    font-size: 13px; line-height: 1.5;
  }
  .ci-alert-banner div { display: flex; flex-direction: column; gap: 2px; }
  .ci-alert-banner strong { font-size: 14px; }
  .ci-alert-banner span { color: #fca5a5; font-size: 12px; }
  .ci-alert-critical { background: #7f1d1d40; border: 1px solid #991b1b; color: #fca5a5; }

  /* Tabs */
  .ci-tabs {
    display: flex; gap: 4px; margin-bottom: 20px;
    background: #111827; border-radius: 10px; padding: 4px; width: fit-content;
  }
  .ci-tab {
    padding: 8px 20px; border-radius: 8px; border: none;
    background: transparent; color: #64748b; cursor: pointer;
    font-size: 13px; font-weight: 500; transition: all 0.15s;
  }
  .ci-tab:hover { color: #94a3b8; }
  .ci-tab.active { background: #3b82f6; color: white; }

  /* Grid & Cards */
  .ci-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .ci-card {
    background: #111827; border: 1px solid #1e293b; border-radius: 12px; padding: 20px;
  }
  .ci-card-wide { grid-column: 1 / -1; }
  .ci-card-title { font-size: 15px; font-weight: 600; color: #e2e8f0; margin: 0 0 16px; }
  .ci-card-subtitle { font-size: 13px; font-weight: 600; color: #94a3b8; margin: 0 0 10px; }
  .ci-card-desc { font-size: 12px; color: #64748b; margin: -8px 0 16px; }
  .ci-empty { text-align: center; color: #475569; padding: 40px 20px; font-size: 13px; }

  /* Evidence Strength */
  .ci-strength-grid { display: flex; flex-direction: column; gap: 10px; }
  .ci-strength-item { display: flex; align-items: center; gap: 10px; }
  .ci-strength-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
  .ci-strength-label { font-size: 13px; color: #94a3b8; flex: 1; }
  .ci-strength-value { font-size: 15px; font-weight: 700; color: #f8fafc; }

  /* Tables */
  .ci-table-wrap { overflow-x: auto; }
  .ci-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .ci-table th {
    text-align: left; padding: 10px 12px; color: #64748b;
    border-bottom: 1px solid #1e293b; font-weight: 500; text-transform: uppercase;
    font-size: 11px; letter-spacing: 0.5px;
  }
  .ci-table td { padding: 10px 12px; border-bottom: 1px solid #1e293b15; color: #cbd5e1; }
  .ci-table tr:hover td { background: #1e293b40; }
  .ci-time { color: #64748b; font-size: 12px; white-space: nowrap; }

  /* Badges */
  .ci-count-badge {
    padding: 2px 10px; border-radius: 99px; font-weight: 700; font-size: 13px;
  }
  .ci-flag-badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 10px; border-radius: 6px; font-size: 12px; font-weight: 600;
  }
  .ci-flag-danger { background: #ef444420; color: #fca5a5; }
  .ci-flag-ok { background: #10b98120; color: #6ee7b7; }
  .ci-type-badge {
    padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 500;
    white-space: nowrap;
  }
  .ci-strength-pill {
    padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 500;
  }

  /* Alerts List */
  .ci-alerts-list { display: flex; flex-direction: column; gap: 10px; }
  .ci-alert-item {
    display: flex; align-items: center; gap: 12px;
    padding: 12px 16px; border-radius: 10px; border: 1px solid transparent;
  }
  .ci-alert-critical { background: #7f1d1d20; border-color: #991b1b60; }
  .ci-alert-warning { background: #78350f20; border-color: #92400e60; }
  .ci-alert-icon { flex-shrink: 0; }
  .ci-alert-item.ci-alert-critical .ci-alert-icon { color: #fca5a5; }
  .ci-alert-item.ci-alert-warning .ci-alert-icon { color: #fcd34d; }
  .ci-alert-content { flex: 1; display: flex; flex-direction: column; gap: 2px; }
  .ci-alert-type-badge {
    font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px;
    color: #64748b; font-weight: 600;
  }
  .ci-alert-msg { font-size: 13px; color: #e2e8f0; }
  .ci-severity-badge {
    padding: 3px 10px; border-radius: 6px; font-size: 11px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.5px;
  }
  .ci-sev-critical { background: #ef444430; color: #fca5a5; }
  .ci-sev-warning { background: #f59e0b30; color: #fcd34d; }

  @media (max-width: 768px) {
    .ci-dash { padding: 16px; }
    .ci-grid-2 { grid-template-columns: 1fr; }
    .ci-header { flex-direction: column; }
    .ci-stats-grid { grid-template-columns: 1fr 1fr; }
  }
`;
